/**
 * Tipos compartidos con el backend. Reflejan el schema Prisma.
 */

export type UserRole = 'agente' | 'gerente' | 'admin' | 'supervisor' | 'ventas' | 'cobranza' | 'director';

export type Estado = 'sin_iniciar' | 'en_proceso' | 'completado' | 'detenido';

export const ESTADOS: Estado[] = ['sin_iniciar', 'en_proceso', 'completado', 'detenido'];

export const ESTADO_LABEL: Record<Estado, string> = {
  sin_iniciar: 'Sin iniciar',
  en_proceso: 'En proceso',
  completado: 'Completado',
  detenido: 'Detenido',
};

// Estado de envío del shipment (manual). Ciclo: origin → in_transit → arrived → closed.
export type EstadoEnvio = 'origin' | 'in_transit' | 'arrived' | 'closed';

export const ESTADOS_ENVIO: EstadoEnvio[] = ['origin', 'in_transit', 'arrived', 'closed'];

export const ESTADO_ENVIO_LABEL: Record<EstadoEnvio, string> = {
  origin: 'Origin',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  closed: 'Closed',
};

/** Códigos de color del estado de envío (paleta definida por operaciones,
 *  15-sep-2026): Origin pálido → In transit → Arrived naranja → Closed gris. */
export const ESTADO_ENVIO_COLOR: Record<EstadoEnvio, { bg: string; text: string }> = {
  origin: { bg: '#FBE5D6', text: '#7a4a21' },
  in_transit: { bg: '#F8CBAD', text: '#7a3c10' },
  arrived: { bg: '#ED7D31', text: '#ffffff' },
  closed: { bg: '#D9D9D9', text: '#444444' },
};

/** Mapeo fase de la tarea → etapa del embarque (validado por Fer, 28-ago-2026).
 *  Seguimiento-Administrativo devuelve null a propósito: se ve normal en TODAS
 *  las etapas ("hay casos que deben facturar desde que zarpa"). Fases
 *  desconocidas o vacías también → null (nunca se atenúa por error). */
export function faseToEstadoEnvio(fase: string | null | undefined): EstadoEnvio | null {
  if (!fase) return null;
  const f = fase
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // sin acentos: Tránsito → Transito
    .trim()
    .toLowerCase();
  const MAPA: Record<string, EstadoEnvio> = {
    // fases de la plantilla original
    'analisis': 'origin',
    'revision de credito': 'origin',
    'booking': 'origin',
    'conversion': 'origin',
    'shipment': 'origin',
    'seguimiento-origen': 'origin',
    'seguimiento-transito': 'in_transit',
    'seguimiento-arribo': 'arrived',
    'seguimiento-entrega': 'arrived',
    'seguimiento-cierre': 'closed',
    // fases de las tareas resumidas (FCL/LCL) — ya vienen con nombre de etapa
    'origen': 'origin',
    'transito': 'in_transit',
    'destino': 'arrived',
    'cerrado': 'closed',
  };
  return MAPA[f] ?? null;
}

// Reporte de bookings — Estatus COMPUTADO (por ETD/ETA + pendiente financiero).
export type EstatusReporte =
  | 'abierto'
  | 'en_transito'
  | 'pre_arribo'
  | 'arribado'
  | 'vencido'
  | 'cerrado'
  | 'sin_fecha';

export const ESTATUS_REPORTE: EstatusReporte[] = [
  'abierto',
  'en_transito',
  'pre_arribo',
  'arribado',
  'vencido',
  'cerrado',
  'sin_fecha',
];

export const ESTATUS_REPORTE_LABEL: Record<EstatusReporte, string> = {
  abierto: 'Abierto',
  en_transito: 'En tránsito',
  pre_arribo: 'Pre-Arribo',
  arribado: 'Arribado',
  vencido: 'Vencido',
  cerrado: 'Cerrado',
  sin_fecha: 'Sin fecha',
};

export interface ReporteBookingRow {
  id: number; // id interno del booking (hilo de comentarios)
  posicion: string;
  tipo: string;
  producto: string;
  origenPais: string | null;
  destinoPais: string | null;
  cliente: string;
  comercial: string | null;
  operativo: string;
  fechaCreacionBO: string | null;
  etd: string | null;
  eta: string | null;
  /** Journey ETD/ETA (puerto→puerto): "C.ETD"/"C.ETA" */
  cEtd: string | null;
  cEta: string | null;
  estatus: EstatusReporte; // Estatus Embarque
  estatusFacturacion: string; // Facturado | Pendiente
  estatusPago: string | null; // Pagado | No pagado
  diasCredito: number | null;
  facturasVencidas: boolean;
  detenido: boolean;
  comentario: string | null;
  comentariosCount: number;
}

export interface ReporteOperativoRow {
  operativo: string;
  cliente: string;
  posicion: string;
  statusEmbarque: EstatusReporte;
  fechaShipment: string | null;
  completadas: number;
  pendientes: number;
  vencidas: number;
}

export interface OperativoDetalleRow {
  tarea: string;
  fase: string | null;
  alta: string | null;
  completada: string | null;
  marcadoPor: string | null;
  limite: string | null;
  dias: number | null;
  tiempoTrabajando: number | null;
  resultado: 'A tiempo' | 'Tarde' | 'Pendiente' | 'Vencida';
  comentario: string | null;
}

export interface User {
  id: number;
  email: string;
  nombre: string;
  rol: UserRole;
  /** Gerentes: 'operaciones' | 'ventas' (los de ventas ven el panel Clientes). */
  gerenteTipo?: string | null;
  avatarUrl?: string | null;
  activo: boolean;
  /** Cuenta root (ROOT_EMAILS): habilita el panel "Insumos & Pipeline". */
  isRoot?: boolean;
  /** Rol ventas: comerciales (Sales Rep) cuyas carteras ve (varios = inside). */
  comercialNombres?: string[];
  /** "Ver como" habilitado (solo staging + admin). */
  impersonacion?: boolean;
}

// ─── Pipeline / Insumos (panel root) ───────────────────────────────────────
export type PipelineFileStatus = 'ok' | 'missing' | 'stale';

export interface PipelineFileStat {
  present: boolean | null;
  chosenName: string | null;
  sizeBytes: number | null;
  /** epoch en segundos (UTC). */
  mtime: number | null;
}

export interface PipelineFile {
  nombre: string;
  grupo: string;
  requerido: boolean;
  origen: PipelineFileStat;
  raw: PipelineFileStat;
  status: PipelineFileStatus;
}

export interface PipelineFilesResponse {
  checkedAt?: string;
  running: boolean;
  files: PipelineFile[];
  /** false si el control server no respondió (degradación suave, no error). */
  available?: boolean;
  reason?: string;
}

export interface PipelineFileCheck {
  id: number;
  runToken: string | null;
  trigger: string;
  checkedAt: string;
  archivos: PipelineFile[];
  okCount: number;
  missingCount: number;
  staleCount: number;
}

// ─── Auditoría & Restauración (panel root) ─────────────────────────────────
export interface AuditOperation {
  opKey: string;
  batchId: string | null;
  changedAt: string;
  userId: number;
  email: string | null;
  estadoNuevo: string;
  source: string;
  count: number;
  bookings: number;
}

export interface AuditUserSummary {
  userId: number;
  email: string | null;
  cambios: number;
  operaciones: number;
}

export interface AuditTimeline {
  operaciones: AuditOperation[];
  porUsuario: AuditUserSummary[];
}

export interface RestorePreview {
  tareas: number;
  bookings: number;
  porDestino: Record<string, number>;
}

/** Una sub-fila del desglose: charge code con su descripción, monto y moneda. */
export interface DesgloseDetalle {
  code: string;
  description: string;
  amount: number;
  currency: string;
}

export interface BookingListItem {
  id: number;
  bookingIdExterno: string;
  clienteNombre: string;
  transporte: string;
  modo: string;
  direccion: string;
  comercialNombre: string | null;
  fechaRecepcion: string | null;
  etd: string | null;
  eta: string | null;
  fechaLimite: string | null;
  /** Próxima fecha-límite de una subtarea pendiente del booking (computado) */
  proximaFechaLimite: string | null;
  /** Tarea "Facturación a Cliente" completada (para ocultar desglose/pte). */
  facturacionCompletada?: boolean;
  /** Tarea "Posteo de Facturas del Agente o Proveedor" completada. */
  posteoCompletado?: boolean;
  /** Vista rápida: campos clave del Shipment Profile (Reportes·Operaciones). */
  opsInfo?: {
    shipper?: string | null;
    cnee?: string | null;
    master?: string | null;
    houseRef?: string | null;
    origenPuerto?: string | null;
    destinoPuerto?: string | null;
    container?: string | null;
    linea?: string | null;
    vesselFlight?: string | null;
  } | null;
  cwStatus: string | null;
  /** Nivel de servicio (código: STD, DIR, CUS, INS, TSP, …) */
  serviceLevel: string | null;
  /** Estado de envío del shipment (manual) */
  estadoEnvio: EstadoEnvio;
  /** Montos pendientes (pueden ser negativos) */
  pteFacturar: number | null;
  pteProvisionar: number | null;
  /** Fechas límite calculadas */
  limiteFacturar: string | null;
  limiteProvisionar: string | null;
  /** Desglose de charge codes para facturación (formato: "1053: $3,100.00 USD | 1070: $795.00 USD") */
  desgloseFacturar?: string | null;
  /** Desglose de charge codes para provisión (formato: "1053: $1,700.00 USD") */
  desgloseProvisionar?: string | null;
  /** Desglose estructurado (sub-filas) por charge code */
  desgloseFacturarDetalle?: DesgloseDetalle[] | null;
  desgloseProvisionarDetalle?: DesgloseDetalle[] | null;
  estadoProgreso: Estado;
  /** Detención de carga (desde el reporte de Bookings). */
  detenido: boolean;
  detenidoAt: string | null;
  detenidoPor: string | null;
  /** Comentario libre escrito en el reporte de Bookings. */
  comentarioReporte: string | null;
  archivado: boolean;
  /** Marcado cuando el booking dejó de aparecer en imports y no estaba completado */
  cancelado: boolean;
  canceladoAt: string | null;
  agente: { id: number; email: string; nombre: string; avatarUrl?: string | null };
  _count: { items: number; comentarios: number };
  createdAt: string;
  updatedAt: string;
}

export interface BookingDetail extends BookingListItem {}

/** Comentario del hilo (chat) de un booking. */
export interface BookingComentario {
  id: number;
  texto: string;
  createdAt: string;
  autor: { id: number; nombre: string; email: string; avatarUrl?: string | null };
}

export interface Item {
  id: number;
  bookingId: number;
  parentId: number | null;
  level: number;
  hierarchyId: string;
  sortKey: string;
  nombre: string;
  tipo: string;
  fase: string | null;
  templateKey: string;
  estado: Estado;
  cwActividadStatus: string | null;
  cwStatusEnReporte: string | null;
  medioActividad: string | null;
  fechaLimite: string | null;
  notasSla: string | null;
  predecesoras: string | null;
  revisionAutomatica: boolean;
  sheetRowHash: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { children: number };
}

export interface MyWorkItem extends Item {
  booking: {
    id: number;
    bookingIdExterno: string;
    clienteNombre: string;
    transporte: string;
    modo: string;
    direccion: string;
  };
}

export interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  items: T[];
  pagination: Pagination;
}

export interface StatusChange {
  id: number;
  itemId: number;
  estadoPrevio: Estado;
  estadoNuevo: Estado;
  changedAt: string;
  user: { id: number; email: string; nombre: string };
}

/** Nota append-only de un agente en un item (evidencia / bloqueo / observación). */
export interface ItemNote {
  id: number;
  itemId: number;
  texto: string;
  createdAt: string;
  user: { id: number; email: string; nombre: string };
  /** Solo presente cuando viene de /bookings/:id/notes (timeline cross-item) */
  item?: { id: number; hierarchyId: string; nombre: string; fase: string | null };
}

export interface SyncRun {
  id: number;
  iniciadoAt: string;
  terminadoAt: string | null;
  estado: string;
  filasLeidas: number;
  bookingsNuevos: number;
  bookingsUpdate: number;
  itemsNuevos: number;
  errores: number;
  warnings: number;
  log: string | null;
  trigger: string;
}
